import { create } from 'zustand';
import type { LoginResponse, Permission, RegisterInput, RegisterResponse } from '@shared/types';
import { ApiError, api, setUnauthorizedHandler } from '../lib/api';

/**
 * 会话状态。
 *
 * 会话本体是 httpOnly Cookie，前端拿不到也不该拿到；这里只缓存
 * 「当前用户 + 所属组织 + 权限」，用于渲染导航与按钮的可用性。
 * 任何接口返回 401 都会把状态重置为匿名，避免界面停在过期视图上。
 */

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  session: LoginResponse | null;
  /** 最近一次会话刷新失败的原因，用于登录页给出提示 */
  error: string | null;
  bootstrap: () => Promise<void>;
  register: (input: RegisterInput) => Promise<RegisterResponse>;
  login: (email: string, password: string, options?: { code?: string; remember?: boolean }) => Promise<void>;
  loginWithRecoveryCode: (email: string, password: string, recoveryCode: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  switchOrg: (tenantId: string) => Promise<void>;
  refresh: () => Promise<void>;
  markAnonymous: () => void;
  can: (permission: Permission) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  session: null,
  error: null,

  async bootstrap() {
    try {
      const session = await api.get<LoginResponse>('/api/auth/me');
      set({ status: 'authenticated', session, error: null });
    } catch (error) {
      // 服务端故障要能被看见，其余情况一律静默降级为未登录。
      const message = error instanceof ApiError && error.status >= 500 ? error.message : null;
      set({ status: 'anonymous', session: null, error: message });
    }
  },

  async register(input) {
    const session = await api.post<RegisterResponse>('/api/auth/register', input);
    set({ status: 'authenticated', session, error: null });
    return session;
  },

  async login(email, password, options) {    const session = await api.post<LoginResponse>('/api/auth/login', {
      email,
      password,
      code: options?.code ?? '',
      remember: options?.remember ?? false,
    });
    set({ status: 'authenticated', session, error: null });
  },

  async loginWithRecoveryCode(email, password, recoveryCode) {
    const session = await api.post<LoginResponse>('/api/auth/totp/recover', {
      email,
      password,
      recoveryCode,
    });
    set({ status: 'authenticated', session, error: null });
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      set({ status: 'anonymous', session: null });
    }
  },

  async changePassword(currentPassword, newPassword) {
    const session = await api.post<LoginResponse>('/api/auth/password', {
      currentPassword,
      newPassword,
    });
    set({ status: 'authenticated', session, error: null });
  },

  async switchOrg(tenantId) {
    const session = await api.post<LoginResponse>('/api/auth/org', { tenantId });
    set({ status: 'authenticated', session, error: null });
  },

  async refresh() {
    if (get().status !== 'authenticated') return;
    const session = await api.get<LoginResponse>('/api/auth/me');
    set({ status: 'authenticated', session, error: null });
  },

  markAnonymous() {
    set({ status: 'anonymous', session: null });
  },

  can(permission) {
    return get().session?.permissions.includes(permission) ?? false;
  },
}));

/**
 * 全局 401 处理：注册到 API 客户端，任何请求遇到会话失效都会
 * 立刻把状态重置为匿名，界面随即回到登录页。
 */
export function registerUnauthorizedHandler(): () => void {
  setUnauthorizedHandler(() => {
    useAuthStore.getState().markAnonymous();
  });
  return () => setUnauthorizedHandler(null);
}
